/**
 * Sendblue API Integration
 * Provides SMS and calling functionality via Sendblue API
 * Messages are stored in Firestore for history (via webhooks for inbound, directly for outbound)
 */

import { getSendblueCredentials } from './appSettings';
import { collection, query, where, orderBy, getDocs, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { db } from './firebase';

const SENDBLUE_API_URL = 'https://api.sendblue.co/api';
const SMS_MESSAGES_COLLECTION = 'smsMessages';

export interface SendSMSResponse {
  success: boolean;
  error?: string;
  messageId?: string;
}

export interface SendblueMessage {
  id: string;
  content: string;
  number: string;        // Phone number
  is_outbound: boolean;  // true = sent by us, false = received
  status: string;        // sent, delivered, read, failed, etc.
  date_sent: string;     // ISO timestamp
  date_updated?: string;
  media_url?: string;    // For MMS
  error_message?: string;
  // Message type
  send_style?: string;   // "imessage", "sms", or "call"
  was_downgraded?: boolean; // true if iMessage failed and fell back to SMS
  // Contact info from Sendblue
  contact_name?: string;
  contact_email?: string;
  // CRM matching
  matchedLeadId?: string;
  // Call-specific fields (when messageType === 'call' or send_style === 'call')
  message_type?: string;       // 'call' for call log entries, 'payment' for payments
  call_duration?: number;      // Duration in seconds
  call_duration_formatted?: string;  // e.g., "2:35"
  call_status?: string;        // answered, missed, voicemail, etc.
  call_status_display?: string; // Human-readable status
  recording_url?: string;      // Call recording URL
  voicemail_url?: string;      // Voicemail URL
  // Payment-specific fields (when messageType === 'payment' or send_style === 'payment')
  payment_amount?: number;           // Payment amount in cents/dollars
  payment_amount_formatted?: string; // e.g., "$2,500.00"
  payment_method?: string;           // Payment method name
  payment_id?: string;               // Reference to payments collection
  quickbooks_payment_id?: string;    // QuickBooks payment ID
}

// Firestore message document structure
export interface StoredMessage {
  id?: string;
  messageId: string;
  content: string;
  phoneNumber: string;
  normalizedPhone: string;
  isOutbound: boolean;
  status: string;
  sendStyle: string;
  wasDowngraded: boolean;
  mediaUrl: string | null;
  dateSent: Date;
  dateUpdated: Date | null;
  receivedAt: Date;
  errorCode: number | null;
  errorMessage: string | null;
  matchedLeadId: string | null;
  // For outbound messages sent from CRM
  sentByUserId?: string;
  sentByUserName?: string;
}

export interface SendblueContact {
  number: string;
  name?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface FetchMessagesResponse {
  success: boolean;
  messages: SendblueMessage[];
  error?: string;
}

/**
 * Format phone number to E.164 format
 * E.164 format: +[country code][number] (e.g., +15551234567)
 */
export function formatPhoneNumber(phone: string): string {
  if (!phone) {
    throw new Error('Phone number is required');
  }

  // Remove all non-digit characters except +
  let cleaned = phone.replace(/[^\d+]/g, '');

  // If it already starts with +, return as is (assuming it's already formatted)
  if (cleaned.startsWith('+')) {
    return cleaned;
  }

  // If it starts with 1 and is 11 digits, assume US number
  if (cleaned.startsWith('1') && cleaned.length === 11) {
    return `+${cleaned}`;
  }

  // If it's 10 digits, assume US number and add +1
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  }

  // If it's 11 digits starting with 1, add +
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+${cleaned}`;
  }

  // Default: add +1 for US numbers
  return `+1${cleaned}`;
}

/**
 * Validate phone number format
 */
export function validatePhoneNumber(phone: string): boolean {
  if (!phone) {
    return false;
  }

  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // E.164 format: + followed by 1-15 digits
  const e164Regex = /^\+[1-9]\d{1,14}$/;
  return e164Regex.test(cleaned);
}

/**
 * Send SMS via Sendblue API and save to Firestore
 */
export async function sendSMS(
  _to?: string,
  _body?: string,
  _leadId?: string,
  _userId?: string,
  _userName?: string,
  _contactFirstName?: string,
  _contactLastName?: string
): Promise<SendSMSResponse> {
  return {
    success: false,
    error: 'Outbound SMS from the CRM has been removed.',
  };
}

/**
 * Fetch all messages from Firestore (inbound via sendblueWebhook).
 */
export async function fetchAllMessages(): Promise<FetchMessagesResponse> {
  try {
    const messagesRef = collection(db, SMS_MESSAGES_COLLECTION);
    const q = query(messagesRef, orderBy('dateSent', 'desc'));
    const snapshot = await getDocs(q);
    
    const messages: SendblueMessage[] = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        content: data.content || '',
        number: data.phoneNumber || '',
        is_outbound: data.isOutbound || false,
        status: data.status || 'unknown',
        date_sent: data.dateSent?.toDate?.()?.toISOString() || new Date().toISOString(),
        date_updated: data.dateUpdated?.toDate?.()?.toISOString(),
        media_url: data.mediaUrl,
        error_message: data.errorMessage,
        send_style: data.sendStyle,
        was_downgraded: data.wasDowngraded,
      };
    });
    
    return {
      success: true,
      messages,
    };
  } catch (error) {
    console.error('Error fetching messages from Firestore:', error);
    return {
      success: false,
      messages: [],
      error: error instanceof Error ? error.message : 'Failed to fetch messages',
    };
  }
}

/**
 * Fetch messages for a specific phone number from Firestore
 */
export async function fetchMessagesForNumber(phoneNumber: string): Promise<FetchMessagesResponse> {
  // Normalize phone number for matching
  const normalizedPhone = phoneNumber.replace(/^\+1/, '').replace(/\D/g, '').slice(-10);
  
  if (!normalizedPhone || normalizedPhone.length < 10) {
    return {
      success: false,
      messages: [],
      error: 'Invalid phone number',
    };
  }

  try {
    const messagesRef = collection(db, SMS_MESSAGES_COLLECTION);
    const q = query(
      messagesRef, 
      where('normalizedPhone', '>=', normalizedPhone),
      where('normalizedPhone', '<=', normalizedPhone + '\uf8ff'),
      orderBy('normalizedPhone'),
      orderBy('dateSent', 'desc')
    );
    
    const snapshot = await getDocs(q);
    
    const messages: SendblueMessage[] = snapshot.docs
      .filter(doc => {
        const data = doc.data();
        const docPhone = (data.normalizedPhone || '').slice(-10);
        return docPhone === normalizedPhone;
      })
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          content: data.content || '',
          number: data.phoneNumber || '',
          is_outbound: data.isOutbound || false,
          status: data.status || 'unknown',
          date_sent: data.dateSent?.toDate?.()?.toISOString() || new Date().toISOString(),
          date_updated: data.dateUpdated?.toDate?.()?.toISOString(),
          media_url: data.mediaUrl,
          error_message: data.errorMessage,
          send_style: data.sendStyle,
          was_downgraded: data.wasDowngraded,
        };
      });
    
    return {
      success: true,
      messages,
    };
  } catch (error) {
    console.error('Error fetching messages from Firestore:', error);
    return {
      success: false,
      messages: [],
      error: error instanceof Error ? error.message : 'Failed to fetch messages',
    };
  }
}

/**
 * Fetch messages for a specific lead by leadId from Firestore
 */
export async function fetchMessagesForLead(leadId: string): Promise<FetchMessagesResponse> {
  try {
    const messagesRef = collection(db, SMS_MESSAGES_COLLECTION);
    const q = query(
      messagesRef, 
      where('matchedLeadId', '==', leadId),
      orderBy('dateSent', 'asc')
    );
    
    const snapshot = await getDocs(q);
    
    const messages: SendblueMessage[] = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        content: data.content || '',
        number: data.phoneNumber || '',
        is_outbound: data.isOutbound || false,
        status: data.status || 'unknown',
        date_sent: data.dateSent?.toDate?.()?.toISOString() || new Date().toISOString(),
        date_updated: data.dateUpdated?.toDate?.()?.toISOString(),
        media_url: data.mediaUrl,
        error_message: data.errorMessage,
        send_style: data.sendStyle,
        was_downgraded: data.wasDowngraded,
      };
    });
    
    // Deduplicate messages
    const dedupedMessages = deduplicateMessages(messages);
    
    return {
      success: true,
      messages: dedupedMessages,
    };
  } catch (error) {
    console.error('Error fetching messages for lead:', error);
    return {
      success: false,
      messages: [],
      error: error instanceof Error ? error.message : 'Failed to fetch messages',
    };
  }
}

/**
 * Normalize message content for deduplication comparison
 * Handles whitespace variations, case differences, and unicode normalization
 */
function normalizeContentForDedup(content: string): string {
  return content
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')  // Collapse multiple whitespace to single space
    .normalize('NFC');      // Unicode normalization
}

/**
 * Deduplicate messages based on content, direction, phone number, and approximate timestamp
 * Keeps the first occurrence of each duplicate group
 * Cloud Function and Sendblue webhooks can create duplicate Firestore documents for the same message
 */
function deduplicateMessages(messages: SendblueMessage[]): SendblueMessage[] {
  const seen = new Map<string, SendblueMessage>();
  let duplicateCount = 0;
  const duplicateDetails: Array<{content: string; ids: string[]; timeDiff: number}> = [];
  
  for (const message of messages) {
    const timestamp = new Date(message.date_sent).getTime();
    // Normalize content more aggressively and include phone number
    const normalizedContent = normalizeContentForDedup(message.content);
    const phoneNormalized = message.number.replace(/\D/g, '').slice(-10); // Last 10 digits
    const contentKey = `${phoneNormalized}_${normalizedContent}_${message.is_outbound}`;
    
    // Check if we've seen this exact content+phone+direction combination recently (within 5 minutes)
    let isDuplicate = false;
    const entries = Array.from(seen.entries());
    for (const [existingKey, existingMsg] of entries) {
      // Extract the content part from existing key for comparison
      const existingKeyBase = existingKey.substring(0, existingKey.lastIndexOf('_'));
      if (existingKeyBase === contentKey) {
        const existingTime = new Date(existingMsg.date_sent).getTime();
        const timeDiff = Math.abs(timestamp - existingTime);
        
        // If same content+phone+direction within 5 minutes, it's a duplicate
        // Increased from 2 minutes to catch more duplicates from async processes
        if (timeDiff < 300000) { // 5 minutes = 300,000 ms
          isDuplicate = true;
          duplicateCount++;
          duplicateDetails.push({
            content: message.content.substring(0, 30),
            ids: [existingMsg.id, message.id],
            timeDiff
          });
          
          // Keep the message with earlier timestamp
          if (timestamp < existingTime) {
            seen.delete(existingKey);
            seen.set(`${contentKey}_${timestamp}`, message);
          }
          break;
        }
      }
    }
    
    if (!isDuplicate) {
      seen.set(`${contentKey}_${timestamp}`, message);
    }
  }
  
  const deduped = Array.from(seen.values()).sort((a, b) => 
    new Date(a.date_sent).getTime() - new Date(b.date_sent).getTime()
  );
  
  return deduped;
}

/**
 * Subscribe to messages for a lead (real-time updates)
 */
export function subscribeToMessagesForLead(
  leadId: string,
  onMessagesUpdate: (messages: SendblueMessage[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const messagesRef = collection(db, SMS_MESSAGES_COLLECTION);
  const q = query(
    messagesRef, 
    where('matchedLeadId', '==', leadId),
    orderBy('dateSent', 'asc')
  );
  
  return onSnapshot(q, 
    (snapshot) => {
      const messages: SendblueMessage[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          content: data.content || '',
          number: data.phoneNumber || '',
          is_outbound: data.isOutbound || false,
          status: data.status || 'unknown',
          date_sent: data.dateSent?.toDate?.()?.toISOString() || new Date().toISOString(),
          date_updated: data.dateUpdated?.toDate?.()?.toISOString(),
          media_url: data.mediaUrl,
          error_message: data.errorMessage,
          send_style: data.sendStyle,
          was_downgraded: data.wasDowngraded,
        };
      });
      
      // Deduplicate messages before passing to callback
      const dedupedMessages = deduplicateMessages(messages);
      
      onMessagesUpdate(dedupedMessages);
    },
    (error) => {
      console.error('Error in messages subscription:', error);
      onError?.(error);
    }
  );
}

/**
 * Subscribe to messages for a phone number (real-time updates)
 */
export function subscribeToMessagesForPhone(
  phoneNumber: string,
  onMessagesUpdate: (messages: SendblueMessage[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const normalizedPhone = phoneNumber.replace(/^\+1/, '').replace(/\D/g, '').slice(-10);
  
  const messagesRef = collection(db, SMS_MESSAGES_COLLECTION);
  // Note: This query might need an index
  const q = query(
    messagesRef,
    orderBy('dateSent', 'asc')
  );
  
  return onSnapshot(q, 
    (snapshot) => {
      const messages: SendblueMessage[] = snapshot.docs
        .filter(doc => {
          const data = doc.data();
          const docPhone = (data.normalizedPhone || '').slice(-10);
          return docPhone === normalizedPhone;
        })
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            content: data.content || '',
            number: data.phoneNumber || '',
            is_outbound: data.isOutbound || false,
            status: data.status || 'unknown',
            date_sent: data.dateSent?.toDate?.()?.toISOString() || new Date().toISOString(),
            date_updated: data.dateUpdated?.toDate?.()?.toISOString(),
            media_url: data.mediaUrl,
            error_message: data.errorMessage,
            send_style: data.sendStyle,
            was_downgraded: data.wasDowngraded,
          };
        });
      
      // Deduplicate messages before passing to callback
      const dedupedMessages = deduplicateMessages(messages);
      
      onMessagesUpdate(dedupedMessages);
    },
    (error) => {
      console.error('Error in messages subscription:', error);
      onError?.(error);
    }
  );
}

/**
 * Subscribe to ALL messages (for Messages page)
 * Includes both SMS/iMessage and call log entries
 */
export function subscribeToAllMessages(
  onMessagesUpdate: (messages: (SendblueMessage & { matchedLeadId?: string })[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const messagesRef = collection(db, SMS_MESSAGES_COLLECTION);
  const q = query(messagesRef, orderBy('dateSent', 'desc'));
  
  return onSnapshot(q, 
    (snapshot) => {
      const messages = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          content: data.content || '',
          number: data.phoneNumber || '',
          is_outbound: data.isOutbound || false,
          status: data.status || 'unknown',
          date_sent: data.dateSent?.toDate?.()?.toISOString() || new Date().toISOString(),
          date_updated: data.dateUpdated?.toDate?.()?.toISOString(),
          media_url: data.mediaUrl,
          error_message: data.errorMessage,
          send_style: data.sendStyle,
          was_downgraded: data.wasDowngraded,
          matchedLeadId: data.matchedLeadId,
          // Call-specific fields
          message_type: data.messageType,
          call_duration: data.callDuration,
          call_duration_formatted: data.callDurationFormatted,
          call_status: data.callStatus,
          call_status_display: data.callStatusDisplay,
          recording_url: data.recordingUrl,
          voicemail_url: data.voicemailUrl,
          // Payment-specific fields
          payment_amount: data.paymentAmount,
          payment_amount_formatted: data.paymentAmountFormatted,
          payment_method: data.paymentMethod,
          payment_id: data.paymentId,
          quickbooks_payment_id: data.quickbooksPaymentId,
        };
      });
      
      // Deduplicate messages before passing to callback
      const dedupedMessages = deduplicateMessages(messages);
      
      onMessagesUpdate(dedupedMessages);
    },
    (error) => {
      console.error('Error in all messages subscription:', error);
      onError?.(error);
    }
  );
}

/**
 * Check if Sendblue is configured
 */
export async function isSendblueConfigured(): Promise<boolean> {
  const { apiKey, apiSecret } = await getSendblueCredentials();
  return !!(apiKey && apiSecret);
}

/**
 * Test Sendblue API connection by using the lookup endpoint
 * Sendblue API: https://api.sendblue.co/api/evaluate
 */
export async function testSendblueConnection(): Promise<{ success: boolean; message: string; details?: unknown }> {
  const { apiKey, apiSecret } = await getSendblueCredentials();
  
  if (!apiKey || !apiSecret) {
    return {
      success: false,
      message: 'API credentials not configured',
    };
  }

  // Simple check - if credentials are configured, assume they're valid
  // The actual validation will happen when sending a message
  return {
    success: true,
    message: 'Sendblue credentials are configured. Connection will be tested when sending a message.',
    details: {
      apiKeyConfigured: !!apiKey,
      apiSecretConfigured: !!apiSecret,
    },
  };
}

/**
 * Initiate a call via Sendblue API
 * Note: Sendblue provides calling functionality, but implementation depends on their API
 */
export async function initiateCall(
  to: string,
  from?: string
): Promise<{ success: boolean; error?: string; callId?: string }> {
  const { apiKey, apiSecret } = await getSendblueCredentials();
  
  if (!apiKey || !apiSecret) {
    return {
      success: false,
      error: 'Sendblue API credentials are not configured. Please add them in Settings > Sendblue.',
    };
  }

  if (!to) {
    return {
      success: false,
      error: 'Phone number is required',
    };
  }

  // Format and validate phone number
  let formattedTo: string;
  try {
    formattedTo = formatPhoneNumber(to);
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Invalid phone number format',
    };
  }

  if (!validatePhoneNumber(formattedTo)) {
    return {
      success: false,
      error: 'Invalid phone number format. Please use E.164 format (e.g., +15551234567)',
    };
  }

  try {
    // Note: This endpoint may vary based on Sendblue's actual API
    // Adjust the endpoint and payload based on Sendblue's documentation
    const response = await fetch(`${SENDBLUE_API_URL}/make-call`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'sb-api-key-id': apiKey,
        'sb-api-secret-key': apiSecret,
      },
      body: JSON.stringify({
        to: formattedTo,
        from: from || formattedTo, // Use 'to' as fallback if 'from' not provided
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error || data.message || `Sendblue API error: ${response.status}`,
      };
    }

    return {
      success: true,
      callId: data.call_id || data.id,
    };
  } catch (error) {
    console.error('Error initiating call via Sendblue:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to initiate call',
    };
  }
}
